// Compute generation depth, arrange members into rows, and assign absolute (x,y)
// canvas coordinates so the tree can be freely panned, zoomed and drag-arranged.

export const CARD_W = 180;
export const CARD_H = 176;
const COUPLE_GAP = 76; // space between two partners (room for the heart)
const UNIT_GAP = 68; // space between separate family units in a row
const ROW_H = 300; // vertical distance between generations

export const THEME_DIMS = {
  classic: { cardW: 180, cardH: 176, coupleGap: 76, unitGap: 68, rowH: 300 },
  compact: { cardW: 148, cardH: 100, coupleGap: 60, unitGap: 46, rowH: 190 },
  photo: { cardW: 188, cardH: 224, coupleGap: 78, unitGap: 56, rowH: 320 },
};

export function computeGenerations(members) {
  const byId = Object.fromEntries(members.map((m) => [m.id, m]));
  const gen = {};
  members.forEach((m) => (gen[m.id] = 0));

  const maxIter = members.length * 2 + 10;
  for (let iter = 0; iter < maxIter; iter++) {
    let changed = false;

    members.forEach((m) => {
      const parents = (m.parent_ids || []).filter((p) => byId[p]);
      if (parents.length) {
        const pg = Math.max(...parents.map((p) => gen[p]));
        if (gen[m.id] !== pg + 1) {
          gen[m.id] = pg + 1;
          changed = true;
        }
      }
    });

    members.forEach((m) => {
      (m.spouse_ids || []).forEach((sid) => {
        if (byId[sid]) {
          const mx = Math.max(gen[m.id], gen[sid]);
          if (gen[m.id] !== mx) { gen[m.id] = mx; changed = true; }
          if (gen[sid] !== mx) { gen[sid] = mx; changed = true; }
        }
      });
    });

    if (!changed) break;
  }
  return gen;
}

// Root ancestor of each member (follow the first parent up) — used for lineage colouring.
export function computeLineageRoots(members) {
  const byId = Object.fromEntries(members.map((m) => [m.id, m]));
  const rootOf = {};
  const resolve = (id, seen = new Set()) => {
    if (rootOf[id]) return rootOf[id];
    if (seen.has(id)) return id;
    seen.add(id);
    const m = byId[id];
    const parent = (m?.parent_ids || []).find((p) => byId[p]);
    const r = parent ? resolve(parent, seen) : id;
    rootOf[id] = r;
    return r;
  };
  members.forEach((m) => resolve(m.id));
  return rootOf;
}

// Order units in a row and assign x/y. Returns { nodes, width, height }.
export function buildLayout(members, gen, dims = {}) {
  const CW = dims.cardW ?? CARD_W;
  const CH = dims.cardH ?? CARD_H;
  const CG = dims.coupleGap ?? COUPLE_GAP;
  const UG = dims.unitGap ?? UNIT_GAP;
  const RH = dims.rowH ?? ROW_H;
  const byId = Object.fromEntries(members.map((m) => [m.id, m]));
  const maxGen = members.length ? Math.max(...Object.values(gen)) : 0;
  const order = {};
  const rowsUnits = [];

  for (let g = 0; g <= maxGen; g++) {
    const people = members.filter((m) => gen[m.id] === g);
    const used = new Set();
    let units = [];
    people.forEach((p) => {
      if (used.has(p.id)) return;
      const group = [p];
      used.add(p.id);
      (p.spouse_ids || []).forEach((sid) => {
        if (byId[sid] && gen[sid] === g && !used.has(sid)) {
          group.push(byId[sid]);
          used.add(sid);
        }
      });
      units.push(group);
    });

    const keyOf = (unit) => {
      const vals = [];
      unit.forEach((m) =>
        (m.parent_ids || []).forEach((p) => {
          if (p in order) vals.push(order[p]);
        })
      );
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : Number.MAX_SAFE_INTEGER;
    };
    if (g > 0) units.sort((a, b) => keyOf(a) - keyOf(b));

    let idx = 0;
    units.forEach((u) => u.forEach((m) => (order[m.id] = idx++)));
    rowsUnits.push(units);
  }

  // measure each row width, then centre rows against the widest one
  const rowWidths = rowsUnits.map((units) => {
    let w = 0;
    units.forEach((u, ui) => {
      w += u.length * CW + (u.length - 1) * CG;
      if (ui < units.length - 1) w += UG;
    });
    return w;
  });
  const totalWidth = Math.max(1, ...rowWidths);

  const nodes = {};
  rowsUnits.forEach((units, g) => {
    let x = (totalWidth - rowWidths[g]) / 2;
    const y = g * RH;
    units.forEach((u) => {
      u.forEach((m, i) => {
        nodes[m.id] = { x, y, auto: { x, y } };
        x += CW;
        if (i < u.length - 1) x += CG;
      });
      x += UG;
    });
  });

  // manual position overrides
  members.forEach((m) => {
    if (typeof m.pos_x === "number" && typeof m.pos_y === "number" && nodes[m.id]) {
      nodes[m.id] = { ...nodes[m.id], x: m.pos_x, y: m.pos_y, manual: true };
    }
  });

  const height = (maxGen + 1) * RH;
  return { nodes, width: totalWidth, height };
}

// ---------- kinship naming ----------
function genderTerm(g, male, female, neutral) {
  const s = (g || "").toLowerCase();
  if (s.startsWith("m")) return male;
  if (s.startsWith("f")) return female;
  return neutral;
}
function greats(n) { return n > 0 ? "great-".repeat(n) : ""; }
function ordinal(n) {
  const words = { 1: "1st", 2: "2nd", 3: "3rd" };
  return words[n] || `${n}th`;
}
function timesWord(n) {
  return { 1: "once", 2: "twice", 3: "thrice" }[n] || `${n} times`;
}

function describeKin(dA, dB, gender) {
  if (dA === 0 && dB === 0) return "the same person";
  if (dA === 0) {
    if (dB === 1) return genderTerm(gender, "son", "daughter", "child");
    if (dB === 2) return genderTerm(gender, "grandson", "granddaughter", "grandchild");
    return greats(dB - 2) + genderTerm(gender, "grandson", "granddaughter", "grandchild");
  }
  if (dB === 0) {
    if (dA === 1) return genderTerm(gender, "father", "mother", "parent");
    if (dA === 2) return genderTerm(gender, "grandfather", "grandmother", "grandparent");
    return greats(dA - 2) + genderTerm(gender, "grandfather", "grandmother", "grandparent");
  }
  if (dA === 1 && dB === 1) return genderTerm(gender, "brother", "sister", "sibling");
  if (dB === 1) return greats(dA - 2) + genderTerm(gender, "uncle", "aunt", "aunt or uncle");
  if (dA === 1) return greats(dB - 2) + genderTerm(gender, "nephew", "niece", "niece or nephew");
  const degree = Math.min(dA, dB) - 1;
  const removed = Math.abs(dA - dB);
  const base = `${ordinal(degree)} cousin`;
  return removed ? `${base} ${timesWord(removed)} removed` : base;
}

// Plain-English kinship of B relative to A ("Joseph is Emma's great-uncle" -> returns "great-uncle").
export function kinshipLabel(members, aId, bId) {
  if (!aId || !bId) return null;
  if (aId === bId) return "the same person";
  const byId = Object.fromEntries(members.map((m) => [m.id, m]));
  const A = byId[aId], B = byId[bId];
  if (!A || !B) return null;

  const ancestors = (id) => {
    const dist = { [id]: 0 };
    const q = [id];
    while (q.length) {
      const cur = q.shift();
      (byId[cur]?.parent_ids || []).forEach((p) => {
        if (byId[p] && !(p in dist)) { dist[p] = dist[cur] + 1; q.push(p); }
      });
    }
    return dist;
  };
  // blood relation label of y relative to x via closest common ancestor
  const commonDegrees = (x, y) => {
    const ax = ancestors(x), ay = ancestors(y);
    let best = null;
    Object.keys(ax).forEach((id) => {
      if (id in ay) {
        const dX = ax[id], dY = ay[id];
        const score = Math.max(dX, dY) * 100 + (dX + dY);
        if (!best || score < best.score) best = { dX, dY, score };
      }
    });
    return best;
  };
  const blood = (x, y) => {
    const best = commonDegrees(x, y);
    return best ? describeKin(best.dX, best.dY, byId[y].gender) : null;
  };
  const isSibling = (x, y) => { const d = commonDegrees(x, y); return !!d && d.dX === 1 && d.dY === 1; };
  const bg = B.gender;

  const direct = blood(aId, bId);
  if (direct) return direct;

  // direct spouse
  if ((A.spouse_ids || []).includes(bId)) return genderTerm(bg, "husband", "wife", "spouse");

  // step-parent: B is married to one of A's parents (and isn't a blood parent)
  for (const pid of A.parent_ids || []) {
    if (byId[pid] && (byId[pid].spouse_ids || []).includes(bId)) return genderTerm(bg, "step-father", "step-mother", "step-parent");
  }
  // step-child: A is married to one of B's parents
  for (const pid of B.parent_ids || []) {
    if (byId[pid] && (byId[pid].spouse_ids || []).includes(aId)) return genderTerm(bg, "step-son", "step-daughter", "step-child");
  }
  // parent-in-law: B is a parent of A's spouse
  for (const sid of A.spouse_ids || []) {
    if (byId[sid] && (byId[sid].parent_ids || []).includes(bId)) return genderTerm(bg, "father-in-law", "mother-in-law", "parent-in-law");
  }
  // child-in-law: B is the spouse of A's child
  for (const c of members) {
    if ((c.parent_ids || []).includes(aId) && (c.spouse_ids || []).includes(bId)) return genderTerm(bg, "son-in-law", "daughter-in-law", "child-in-law");
  }
  // sibling-in-law: B is a sibling of A's spouse
  for (const sid of A.spouse_ids || []) {
    if (isSibling(sid, bId)) return genderTerm(bg, "brother-in-law", "sister-in-law", "sibling-in-law");
  }
  // sibling-in-law: B is the spouse of A's sibling
  for (const s of members) {
    if (s.id !== bId && isSibling(aId, s.id) && (s.spouse_ids || []).includes(bId)) return genderTerm(bg, "brother-in-law", "sister-in-law", "sibling-in-law");
  }

  // generic marriage fallbacks (e.g. "cousin's husband")
  for (const sid of B.spouse_ids || []) {
    if (!byId[sid]) continue;
    const r = blood(aId, sid);
    if (r) return `${r}'s ${genderTerm(bg, "husband", "wife", "spouse")}`;
  }
  for (const sid of A.spouse_ids || []) {
    if (!byId[sid]) continue;
    const r = blood(sid, bId);
    if (r) return `${genderTerm(byId[sid].gender, "husband", "wife", "spouse")}'s ${r}`;
  }
  return "relative (by marriage)";
}

// Descendant id set for a member (to block dropping a person onto its own descendant).
export function descendantSet(members, rootId) {
  const childrenMap = {};
  members.forEach((m) => (m.parent_ids || []).forEach((p) => {
    childrenMap[p] = childrenMap[p] || [];
    childrenMap[p].push(m.id);
  }));
  const out = new Set();
  const queue = [rootId];
  while (queue.length) {
    const cur = queue.pop();
    (childrenMap[cur] || []).forEach((c) => {
      if (!out.has(c)) { out.add(c); queue.push(c); }
    });
  }
  return out;
}
